import React, { useState, useEffect, useCallback } from 'react';
import {
    X, BookOpen, Users, Clock, CheckCircle2,
    TrendingUp, BarChart3, RefreshCw, UserPlus, Award, AlertTriangle, CheckSquare, AlertCircle, ChevronRight, Download
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
    StatCard,
    BarChart,
    DataTable,
    ProgressRing,
    DonutChart,
    AIInsightCard,
    ExpandableVisualization
} from '../Analytics';
import useDrawerResize from './useDrawerResize';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import NotificationSettingsPanel from './NotificationSettingsPanel';

// Helper for formatting time
const formatTime = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
};

const CoursesAnalyticsDrawer = ({ isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState('onboarding'); // 'onboarding' | 'preboarding'
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // ── Resizable drawer ──
    const { drawerWidth, isDragging, dragHandleProps } = useDrawerResize({
        minWidth: 400, maxWidth: 900, defaultWidth: 700,
        storageKey: 'coursesAnalyticsDrawerWidth',
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            // Fetch relevant data based on activeTab.

            let endpoint = activeTab === 'preboarding' ? '/analytics/preboarding' : '/analytics/onboarding';

            const response = await api.get(endpoint, { headers });
            setData(response.data.analytics);

        } catch (error) {
            console.error('Error fetching course analytics:', error);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    const handleDownloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const reportType = activeTab === 'preboarding' ? 'preboarding' : 'onboarding';
            const response = await api.post('/analytics/report', {
                reportType, format: 'csv'
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `${reportType}-analytics.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} analytics exported`);
        } catch { toast.error('Export failed'); }
    };

    useEffect(() => {
        if (isOpen) {
            fetchData();
            document.body.style.overflow = 'hidden';
        } else {
            setData(null);
            document.body.style.overflow = 'unset';
            setLoading(true);
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, fetchData]);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />
            <div
                className="relative h-full bg-[#FAFAF9] shadow-2xl flex flex-col animate-slide-in-right"
                style={{ width: `${drawerWidth}px`, maxWidth: '100vw', transition: isDragging ? 'none' : undefined }}
            >
                {/* ── Drag handle (left edge) ── */}
                <div {...dragHandleProps} />
                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-[#E5E1DD] flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-50 rounded-lg">
                            <BookOpen className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Course & Onboarding Analytics</h2>
                            <p className="text-sm text-gray-500">Track employee progress through learning paths</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadCSV}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download CSV"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white border-b border-[#E5E1DD] px-6">
                    <div className="flex gap-6">
                        <button
                            onClick={() => setActiveTab('onboarding')}
                            className={`py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'onboarding'
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Onboarding
                        </button>
                        <button
                            onClick={() => setActiveTab('preboarding')}
                            className={`py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'preboarding'
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Preboarding
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 space-y-4">
                            <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                            <p className="text-gray-500 font-medium">Loading analytics...</p>
                        </div>
                    ) : data ? (
                        <div className="space-y-6">
                            {activeTab === 'onboarding' && (
                                <>
                                    {/* Average Duration Stats */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                                            <h3 className="font-semibold text-gray-800 mb-1">Average Onboarding Duration</h3>
                                            <p className="text-sm text-gray-500 mb-2">Among employees who completed onboarding</p>
                                            <p className="text-3xl font-bold text-indigo-600">
                                                {data.averageOnboardingDurationDays != null
                                                    ? `${data.averageOnboardingDurationDays} days`
                                                    : '—'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Employees who completed onboarding table */}
                                    {data.onboardingCompletedList && data.onboardingCompletedList.length > 0 && (
                                        <ExpandableVisualization
                                            title="Recently Completed Onboarding"
                                            data={data.onboardingCompletedList}
                                            columns={[{ key: 'name', label: 'Employee' }, { key: 'onboardingStartDate', label: 'Start Date' }, { key: 'onboardingCompleteDate', label: 'Completed' }, { key: 'durationDays', label: 'Duration (Days)' }]}
                                            exportFilename="onboarding-completed"
                                        >
                                            <div className="bg-white rounded-xl border border-[#E5E1DD] overflow-hidden shadow-sm">
                                                <div className="p-4 border-b border-[#E5E1DD]">
                                                    <h3 className="font-semibold text-gray-800">Recently Completed Onboarding</h3>
                                                </div>
                                                <DataTable
                                                    data={data.onboardingCompletedList}
                                                    columns={[
                                                        { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-gray-900">{val}</span> },
                                                        { key: 'onboardingStartDate', label: 'Start Date', render: (val) => val ? new Date(val).toLocaleDateString() : '-' },
                                                        { key: 'onboardingCompleteDate', label: 'Completed', render: (val) => val ? new Date(val).toLocaleDateString() : '-' },
                                                        { key: 'durationDays', label: 'Duration', render: (val) => val ? `${val} days` : '-' }
                                                    ]}
                                                    searchable
                                                    searchKeys={['name']}
                                                    pageSize={5}
                                                />
                                            </div>
                                        </ExpandableVisualization>
                                    )}

                                    {/* Completion by Group Charts */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        <ExpandableVisualization
                                            title="Completion by Department"
                                            data={data.byDepartment || []}
                                            columns={[{ key: 'department', label: 'Department' }, { key: 'completion_rate', label: 'Completion Rate %' }]}
                                            exportFilename="completion-by-department"
                                        >
                                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                                <h3 className="font-semibold text-gray-800 mb-4">Completion by Department</h3>
                                                {data.byDepartment && (
                                                    <BarChart
                                                        data={data.byDepartment.slice(0, 8).map(d => ({
                                                            label: d.department,
                                                            value: d.completion_rate,
                                                            suffix: '%'
                                                        }))}
                                                        horizontal
                                                        barColor="#22c55e"
                                                    />
                                                )}
                                            </div>
                                        </ExpandableVisualization>
                                        <ExpandableVisualization
                                            title="Completion by Role"
                                            data={data.byRole || []}
                                            columns={[{ key: 'position', label: 'Role' }, { key: 'completion_rate', label: 'Completion Rate %' }]}
                                            exportFilename="completion-by-role"
                                        >
                                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                                <h3 className="font-semibold text-gray-800 mb-4">Completion by Role</h3>
                                                {data.byRole && (
                                                    <BarChart
                                                        data={data.byRole.slice(0, 8).map(r => ({
                                                            label: r.position,
                                                            value: r.completion_rate,
                                                            suffix: '%'
                                                        }))}
                                                        horizontal
                                                        barColor="#f59e0b"
                                                    />
                                                )}
                                            </div>
                                        </ExpandableVisualization>
                                    </div>

                                    {/* Time to Complete Table */}
                                    <ExpandableVisualization
                                        title="Individual Progress"
                                        data={data.timeToComplete || []}
                                        columns={[{ key: 'name', label: 'Employee' }, { key: 'status', label: 'Status' }]}
                                        exportFilename="individual-progress"
                                    >
                                        <div className="bg-white rounded-xl border border-[#E5E1DD] overflow-hidden shadow-sm">
                                            <div className="p-4 border-b border-[#E5E1DD]">
                                                <h3 className="font-semibold text-gray-800">Individual Progress</h3>
                                            </div>
                                            <DataTable
                                                data={data.timeToComplete || []}
                                                columns={[
                                                    { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-gray-900">{val}</span> },
                                                    {
                                                        key: 'status', label: 'Status', render: (val) => (
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${val === 'active' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                                                }`}>{val}</span>
                                                        )
                                                    },
                                                    {
                                                        key: 'onboarding', label: 'Onboarding', render: (val) => {
                                                            const p = val || { progress: 0, completed: 0, total: 0 };
                                                            return (
                                                                <div className="flex flex-col gap-1 w-24">
                                                                    <div className="flex justify-between text-xs text-gray-600">
                                                                        <span>{p.progress}%</span>
                                                                        <span>{p.completed}/{p.total}</span>
                                                                    </div>
                                                                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                                        <div className="h-full bg-blue-600 rounded-full" style={{ width: `${p.progress}%` }} />
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    }
                                                ]}
                                                searchable
                                                searchKeys={['name']}
                                                pageSize={10}
                                            />
                                        </div>
                                    </ExpandableVisualization>

                                    {/* Bottlenecks */}
                                    {data.bottlenecks && data.bottlenecks.length > 0 && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                                            <div className="flex items-start gap-3">
                                                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                    <h4 className="font-semibold text-amber-800">Bottleneck Tasks</h4>
                                                    <p className="text-sm text-amber-700 mb-4">Tasks slowing down onboarding completion</p>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        {data.bottlenecks.slice(0, 6).map(task => (
                                                            <div key={task.id} className="bg-white/60 rounded-lg p-3">
                                                                <p className="font-medium text-gray-900 truncate">{task.title}</p>
                                                                <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                                                                    <span>{task.completionRate}% completion</span>
                                                                    <span>{task.avgDaysToComplete} days avg</span>
                                                                    <span className={task.overdue > 0 ? 'text-red-600' : ''}>{task.overdue} overdue</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* AI Insights */}
                                    <AIInsightCard
                                        title="Course & Onboarding Insights"
                                        insight={`Average onboarding duration is ${data.averageOnboardingDurationDays || 0} days. ${data.bottlenecks?.length > 0 ? `Optimization needed: ${data.bottlenecks.length} bottlenecks identified.` : 'Learning paths are efficient.'}`}
                                        suggestions={[
                                            "Which courses have the lowest completion rates?",
                                            "Show me employees struggling with onboarding courses",
                                            "Identify bottlenecks in the onboarding path",
                                            "Compare course completion by department"
                                        ]}
                                        context={{ courses: data, type: 'courses_onboarding' }}
                                    />

                                    {/* AI Chat Panel */}
                                    <AnalyticsAIPanel
                                        analyticsContext={{
                                            type: 'courses',
                                            title: 'Course & Onboarding Analytics',
                                            data: data,
                                        }}
                                    />

                                    {/* Notification Settings Panel */}
                                    <NotificationSettingsPanel
                                        analyticsContext={{
                                            type: 'courses',
                                            title: 'Course & Onboarding Analytics',
                                            data: data,
                                        }}
                                    />
                                </>
                            )}

                            {activeTab === 'preboarding' && (
                                <>
                                    {/* Summary Stats */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <StatCard
                                            title="Preboarding Employees"
                                            value={data.summary?.totalPreboardingEmployees || 0}
                                            subtitle="Currently in preboarding"
                                            icon={UserPlus}
                                            color="amber"
                                        />
                                        <StatCard
                                            title="Task Completion Rate"
                                            value={`${data.summary?.completionRate || 0}%`}
                                            subtitle={`${data.summary?.completedTasks || 0} of ${data.summary?.totalTasks || 0} tasks`}
                                            icon={CheckSquare}
                                            color="green"
                                        />
                                        <StatCard
                                            title="Avg Quiz Score"
                                            value={data.summary?.avgQuizScore ? `${data.summary.avgQuizScore}%` : '-'}
                                            subtitle="Preboarding quizzes"
                                            icon={Award}
                                            color="purple"
                                        />
                                        <StatCard
                                            title="Overdue Tasks"
                                            value={data.summary?.overdueTasks || 0}
                                            subtitle="Require attention"
                                            icon={AlertTriangle}
                                            color={data.summary?.overdueTasks > 0 ? 'red' : 'gray'}
                                        />
                                    </div>

                                    {/* Preboarding Compliance */}
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div>
                                                <h3 className="font-semibold text-gray-800">Preboarding Compliance</h3>
                                                <p className="text-sm text-gray-500">Compliance task completion for preboarding employees</p>
                                            </div>
                                            <ProgressRing
                                                value={data.compliance?.completionRate || 0}
                                                size={80}
                                                label="Complete"
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                                            <div className="text-center">
                                                <p className="text-2xl font-bold text-gray-900">{data.compliance?.totalTasks || 0}</p>
                                                <p className="text-sm text-gray-500">Total Tasks</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-2xl font-bold text-green-600">{data.compliance?.completed || 0}</p>
                                                <p className="text-sm text-gray-500">Completed</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-2xl font-bold text-red-600">{data.compliance?.overdue || 0}</p>
                                                <p className="text-sm text-gray-500">Overdue</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Employee Progress Table */}
                                    <ExpandableVisualization
                                        title="Preboarding Employee Progress"
                                        data={data.employeeProgress || []}
                                        columns={[{ key: 'name', label: 'Employee' }, { key: 'department', label: 'Department' }, { key: 'completionRate', label: 'Completion %' }, { key: 'avgQuizScore', label: 'Avg Score %' }]}
                                        exportFilename="preboarding-progress"
                                    >
                                        <div className="bg-white rounded-xl border border-[#E5E1DD] overflow-hidden shadow-sm">
                                            <div className="p-4 border-b border-[#E5E1DD]">
                                                <h3 className="font-semibold text-gray-800">Preboarding Employee Progress</h3>
                                            </div>
                                            <DataTable
                                                data={data.employeeProgress || []}
                                                columns={[
                                                    { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-gray-900">{val}</span> },
                                                    { key: 'department', label: 'Department' },
                                                    {
                                                        key: 'completionRate', label: 'Progress', render: (val, row) => (
                                                            <div className="flex flex-col gap-1 w-24">
                                                                <div className="flex justify-between text-xs text-gray-600">
                                                                    <span>{val}%</span>
                                                                    <span>{row.completedTasks}/{row.totalTasks}</span>
                                                                </div>
                                                                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-amber-500 rounded-full" style={{ width: `${val}%` }} />
                                                                </div>
                                                            </div>
                                                        )
                                                    },
                                                    { key: 'avgQuizScore', label: 'Avg Score', render: (val) => val ? `${val}%` : '-' }
                                                ]}
                                                searchable
                                                searchKeys={['name']}
                                                pageSize={5}
                                            />
                                        </div>
                                    </ExpandableVisualization>

                                    {/* Bottlenecks */}
                                    {data.bottlenecks && data.bottlenecks.length > 0 && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                                            <div className="flex items-start gap-3">
                                                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                    <h4 className="font-semibold text-amber-800">Preboarding Bottlenecks</h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                                        {data.bottlenecks.slice(0, 6).map(task => (
                                                            <div key={task.id} className="bg-white/60 rounded-lg p-3">
                                                                <p className="font-medium text-gray-900 truncate">{task.title}</p>
                                                                <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                                                                    <span>{task.completionRate}% completion</span>
                                                                    <span className={task.overdue > 0 ? 'text-red-600' : ''}>{task.overdue} overdue</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <AIInsightCard
                                        title="Preboarding Insights"
                                        insight={`${data.summary?.totalPreboardingEmployees || 0} employees are currently in preboarding. ${data.summary?.overdueTasks > 0
                                            ? `There are ${data.summary.overdueTasks} overdue tasks needing attention.`
                                            : 'Tasks are progressing well.'
                                            }`}
                                        suggestions={[
                                            "Which preboarding employees are struggling?",
                                            "Show me compliance status for preboarding"
                                        ]}
                                        context={{ preboarding: data, type: 'preboarding' }}
                                    />

                                    {/* AI Chat Panel */}
                                    <AnalyticsAIPanel
                                        analyticsContext={{
                                            type: 'courses_preboarding',
                                            title: 'Preboarding Analytics',
                                            data: data,
                                        }}
                                    />

                                    {/* Notification Settings Panel */}
                                    <NotificationSettingsPanel
                                        analyticsContext={{
                                            type: 'courses_preboarding',
                                            title: 'Preboarding Analytics',
                                            data: data,
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <div className="bg-gray-50 rounded-lg p-6 max-w-sm mx-auto border border-gray-200">
                                <BookOpen className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                <h3 className="text-gray-900 font-medium mb-1">No Analytics Available</h3>
                                <p className="text-sm text-gray-500">
                                    We couldn't load course analytics at this time.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CoursesAnalyticsDrawer;
